import { components } from "@/lib/openapi";
import { client } from "@/queries/client";
import { useQuery } from "@tanstack/react-query";

export type RetailReport = components["schemas"]["RetailReport"];
export type RetailTakeoff = components["schemas"]["LastFlight"];
export type TrendInfo = components["schemas"]["TrendInfo"];
export type StoreTerminalsResponse =
  components["schemas"]["StoreTerminalsResponse"];

async function fetchRetailReport({
  report,
  stores,
  allStores = false,
  signal,
}: {
  report: number;
  stores: number[];
  allStores?: boolean;
  signal?: AbortSignal;
}) {
  const { data, error } = await client
    .get()
    .POST("/api/retail/summary/{report_id}", {
      signal,
      params: {
        path: {
          report_id: report,
        },
      },
      body: {
        stores,
        all_stores: allStores,
      },
    });
  if (data) return data as RetailReport;
  console.warn(error);
  throw new Error("Failed to fetch retail report");
}

export const getRetailReport = {
  getKey: (params: { report: number; stores: number[]; allStores?: boolean }) =>
    [
      "retail-report",
      params.report,
      params.stores,
      params.allStores ?? false,
    ] as const,

  useQuery: (params: {
    report: number;
    stores: number[];
    allStores?: boolean;
  }) => {
    return useQuery({
      queryKey: getRetailReport.getKey(params),
      queryFn: ({ signal }) => fetchRetailReport({ ...params, signal }),
    });
  },
};

export function getStoreTerminals(params: {
  stores: number[];
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["store-terminals", params.stores],
    enabled: params.enabled ?? true,
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .POST("/api/retail/store_terminals", {
          signal,
          body: {
            stores: params.stores,
          },
        });
      if (data) return data as StoreTerminalsResponse;
      console.warn(error);
      throw new Error("Failed to fetch store terminals");
    },
  });
}

export function getMetricBreakdown(
  reportId: number | null,
  metricName: string | null,
  stores: number[]
) {
  return useQuery({
    enabled: reportId !== null && metricName !== null,
    queryKey: ["metric_breakdown", reportId, metricName, stores],
    queryFn: async ({ signal }) => {
      if (reportId === null || metricName === null)
        throw new Error("Missing parameters");

      const storesParam = stores.length > 0 ? stores.join(",") : "";
      const { data, error } = await client
        .get()
        .GET("/api/retail/{report_id}/metric-breakdown/{metric_name}", {
          signal,
          params: {
            path: {
              report_id: reportId,
              metric_name: metricName,
            },
            query: {
              stores_param: storesParam,
            },
          },
        });

      if (data) return data;
      console.warn(error);
      throw new Error("Failed to fetch metric breakdown");
    },
    staleTime: 0,
    refetchOnMount: "always",
  });
}
